import {
  Field,
  FieldBlock,
  Recap,
  RecapResponse,
  RecapTaskData,
  RecapTaskPropertyKeys,
} from '../../types/recap'
import { Task } from '../../store/useTaskProfiles'
import { logInfo } from '../../utils'

export function useRecapHelpers() {
  function getRequireImageRaiCodesFromTask(task: Task | undefined): string {
    let raiCodes = ''
    if (task) {
      // This is Neptune logic
      switch (parseInt(String(task.task_type_id))) {
        case 1: // Install
        case 2: // Premium Holdover
        case 3: // Standard Holdover
          raiCodes = String(task.initial_image_rai ?? '')
          break
        case 4: // Maintenance
          raiCodes = String(task.maintain_image_rai ?? '')
          break
        case 5: // Remove
          raiCodes = String(task.remove_image_rai ?? '')
          break
      }
    }
    return raiCodes.trim()
  }

  function shouldDisplayImageQuestion(task: Task | undefined, raiCode: string): boolean {
    if (!task) {
      logInfo('error', 'Image Reason Code: No task', 'No current task found for recap', {
        task,
        currentRaiCode: raiCode,
      })
      return false
    }
    const raiCodes = getRequireImageRaiCodesFromTask(task)
    if (raiCodes) {
      const splitRai = raiCodes.split(',').map((code) => code.trim())
      // Handle the case where the given RAI code is `0` instead of `000`
      const completedIndex = splitRai.findIndex((rai) => rai === '0')
      if (completedIndex > -1) {
        splitRai.splice(completedIndex, 1, '000')
      }
      if (!splitRai.includes(raiCode)) {
        if (raiCode === '000') {
          // For some reason, the completed RAI code isn't in the
          // listed RAI codes to display the image question.
          // According to Neptune, this should always display in that scenario.
          logInfo(
            'error',
            'Image Reason Code Not Displaying',
            'Image Reason Code Not Displaying due to no RAI code match',
            { task, raiCodes, splitRai, currentRaiCode: raiCode }
          )

          // Return true so the image qustion displays anyway
          return true
        }
        return false
      } else {
        return true
      }
    }
    return false
  }

  function isImageField(field: Field) {
    return (
      field.field.defined_as === 'FIELD_IMAGE_UPLOAD' ||
      field.field.defined_as === 'FIELD_IMAGE_UPLOAD_MULTIPLE'
    )
  }

  function getTaskResponseExpectedImageInfo(
    definition: Recap,
    response: RecapResponse
  ): ExpectedImageInfo[] {
    const imageFields: ImageFieldInfo[] = []
    definition.pages.forEach((page) => {
      const forms = page.items || page.forms || []
      forms.forEach((form) => {
        const items = form.items || form.fields || []
        items.forEach((fieldOrFieldBlock) => {
          if (isFieldBlock(fieldOrFieldBlock)) {
            fieldOrFieldBlock.items.forEach((field) => {
              if (isImageField(field)) {
                imageFields.push({
                  field_instance_id: field.id,
                  multiple: field.field.defined_as === 'FIELD_IMAGE_UPLOAD_MULTIPLE',
                })
              }
            })
          } else if (isImageField(fieldOrFieldBlock)) {
            imageFields.push({
              field_instance_id: fieldOrFieldBlock.id,
              multiple: fieldOrFieldBlock.field.defined_as === 'FIELD_IMAGE_UPLOAD_MULTIPLE',
            })
          }
        })
      })
    })

    const expectedImageInfo: ExpectedImageInfo[] = []
    response.form_responses.forEach((form) => {
      form.field_responses.forEach((field) => {
        const matchingImageField = imageFields.find(
          (imageField) => imageField.field_instance_id === field.events_custom_instances_id
        )
        if (matchingImageField) {
          // We know this is an image field
          // We're relying on the recap to have correctly prevented recap completion
          // if the image was required. As such, we just want to know how many images to expect.
          // If the value is null, we don't expect any images.
          // If the value isn't null, and it's a single image field, we expect 1 image
          // If the value isn't null, and it's a multiple image field, we expect 1 or more images
          let expected: 0 | 1 | 'multiple' = 0
          if (field.value !== null) {
            expected = matchingImageField.multiple ? 'multiple' : 1
          }
          expectedImageInfo.push({
            field_instance_id: matchingImageField.field_instance_id,
            expected,
          })
        }
      })
    })

    return expectedImageInfo
  }

  // Type Guard for Field Blocks
  function isFieldBlock(value: Field | FieldBlock): value is FieldBlock {
    return value.hasOwnProperty('items')
  }

  function getTaskDataKeyForTaskProperty(
    property: RecapTaskPropertyKeys
  ): keyof RecapTaskData | null {
    switch (property) {
      case 'adsInstalled':
        return 'ads_installed'
      case 'bundlesAdded':
        return 'bundles_added'
      case 'piecesRemaining':
        return 'pieces_remaining'
      case 'placementInstructionSequence':
        return 'placement_instruction_sequence'
      case 'quantityInstalled':
        return 'quantity_installed'
      case 'quantityReasonCode':
        return 'quantity_reason_code'
      case 'raiCodeId':
        return 'rai_code'
      case 'raiReasonId':
        return 'rai_reason_code'
      case 'taskComments':
        return 'task_comments'
      default:
        return null
    }
  }

  return {
    isFieldBlock,
    getRequireImageRaiCodesFromTask,
    shouldDisplayImageQuestion,
    getTaskResponseExpectedImageInfo,
    getTaskDataKeyForTaskProperty,
  }
}

type ImageFieldInfo = { field_instance_id: string; multiple: boolean }
type ExpectedImageInfo = { field_instance_id: string; expected: 0 | 1 | 'multiple' }
